import React, { useEffect } from 'react';
import { ArrowLeft, Search, Calendar, FileText, Activity, Layout, Layers, User, AlertCircle, Zap, TrendingUp, CheckCircle, Smartphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import TableOfContents from '../components/TableOfContents';

const NHCareCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'context', label: 'Context & Role' },
    { id: 'problem', label: 'The Problem' },
    { id: 'insights', label: 'Key Insights' },
    { id: 'approach', label: 'Design Approach' },
    { id: 'system', label: 'Design System' },
    { id: 'modules', label: 'Module Improvements' },
    { id: 'impact', label: 'Product Impact' },
    { id: 'screens', label: 'Key Screens' }
  ];

  return (
    <div className="bg-pastel-bg min-h-screen font-sans text-neutral-900 selection:bg-accent/20 relative">
      
      <TableOfContents sections={sections} />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-neutral-600 hover:text-accent transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>
        <div className="h-px bg-neutral-200/60 mx-6 md:mx-12" />
      </nav>

      {/* Hero Banner Image - Full Width */}
      <div className="w-full h-[60vh] md:h-[70vh] bg-neutral-100 mt-20 relative overflow-hidden">
        <img 
          src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/s3va3i4h_Banner.jpg" 
          alt="NH Care App Interface" 
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-pastel-bg to-transparent opacity-20"></div>
      </div>

      <main className="pt-20 pb-20">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          
          {/* 1. Header */}
          <header id="overview" className="mb-20 scroll-mt-32">
            <h1 className="text-4xl md:text-6xl font-serif text-neutral-900 mb-6 leading-tight">
              NH Care - Patient Mobile App
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed max-w-3xl mb-12">
              Optimizing critical patient journeys and establishing a design system for Narayana Health's flagship patient-facing application.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <div className="bg-white p-6 rounded-2xl border border-neutral-100 flex flex-col items-start gap-3">
                <div className="bg-blue-50 p-2 rounded-lg"><Search className="w-5 h-5 text-blue-600" /></div>
                <span className="font-bold text-sm">Discover Doctors</span>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-neutral-100 flex flex-col items-start gap-3">
                <div className="bg-green-50 p-2 rounded-lg"><Calendar className="w-5 h-5 text-green-600" /></div>
                <span className="font-bold text-sm">Book Appointments</span>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-neutral-100 flex flex-col items-start gap-3">
                <div className="bg-orange-50 p-2 rounded-lg"><Activity className="w-5 h-5 text-orange-600" /></div>
                <span className="font-bold text-sm">Schedule Labs</span>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-neutral-100 flex flex-col items-start gap-3">
                <div className="bg-purple-50 p-2 rounded-lg"><FileText className="w-5 h-5 text-purple-600" /></div>
                <span className="font-bold text-sm">Health Records</span>
              </div>
            </div>
          </header>

          {/* 2. Context & Role */}
          <section id="context" className="mb-24 grid grid-cols-1 md:grid-cols-3 gap-12 scroll-mt-32">
            <div className="md:col-span-2">
              <h2 className="text-2xl font-serif mb-6">Context</h2>
              <p className="text-neutral-600 leading-relaxed mb-6">
                The app was already live and widely used across multiple Narayana Health hospitals. However, over time the product evolved through multiple feature additions, which led to fragmented experiences across several key modules.
              </p>
              <p className="text-neutral-600 leading-relaxed">
                This initiative focused on improving high-impact patient journeys and optimizing key modules, while maintaining the existing product ecosystem.
              </p>
            </div>
            <div className="bg-white p-8 rounded-3xl border border-neutral-100 h-fit">
              <h3 className="font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-accent" /> Role: Senior Product Designer
              </h3>
              <ul className="text-sm text-neutral-600 space-y-3">
                <li>• Evaluating existing modules</li>
                <li>• Identifying friction points</li>
                <li>• Redefining IA</li>
                <li>• Designing UI & Interaction</li>
                <li>• Design System Contribution</li>
              </ul>
            </div>
          </section>

          {/* 3. The Problem */}
          <section id="problem" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">The Problem</h2>
            <div className="bg-white p-10 rounded-[2.5rem] border border-neutral-100">
              <p className="text-lg text-neutral-700 mb-8">
                Although the product supported multiple healthcare services, the experience across modules had become inconsistent and difficult to navigate.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  "Difficulty discovering the right doctor quickly",
                  "Limited visibility into appointment availability",
                  "Repetitive steps for follow-up bookings",
                  "Fragmented lab test booking workflows",
                  "Difficulty locating health records",
                  "Inconsistent UI patterns"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <span className="text-neutral-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 4. Research & Insights */}
          <section id="insights" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-12">Key Insights</h2>
            
            <div className="space-y-6">
              {[
                {
                  title: "Doctor discovery required too much effort",
                  desc: "Patients struggled to identify doctors based on specialty/location, navigating multiple screens before finding options."
                },
                {
                  title: "Follow-up consultations were unnecessarily repetitive",
                  desc: "Patients with recurring conditions had to repeat the entire booking workflow every time."
                },
                {
                  title: "Diagnostic results lacked real-time visibility",
                  desc: "Patients manually checked the app repeatedly for reports, despite results being ready."
                },
                {
                  title: "Small manual interactions slowed down tasks",
                  desc: "Selecting locations, investigations, and slots required unnecessary manual effort."
                }
              ].map((card, i) => (
                <div key={i} className="bg-white p-6 rounded-2xl border border-neutral-100 hover:border-accent/30 transition-colors">
                  <h3 className="text-lg font-bold text-neutral-900 mb-2">{card.title}</h3>
                  <p className="text-neutral-500 text-sm leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 5. Design Approach */}
          <section id="approach" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-12">Module-First Approach</h2>
            
            <div className="relative border-l-2 border-neutral-200 ml-4 space-y-12 pl-8 py-2">
              {[
                { step: "Step 1", title: "Map the patient journey", desc: "Analyzed how patients moved across key modules to identify structural gaps." },
                { step: "Step 2", title: "Refine information architecture", desc: "Simplified navigation and relationships between modules." },
                { step: "Step 3", title: "Improve high-impact modules", desc: "Redesigned Discovery, Booking, History, and Dashboard." },
                { step: "Step 4", title: "Automate micro-interactions", desc: "Auto-location, suggestions, faster slot selection." },
                { step: "Step 5", title: "Establish a design system", desc: "Defined components, patterns, and guidelines for consistency." }
              ].map((item, i) => (
                <div key={i} className="relative">
                  <span className="absolute -left-[41px] top-0 w-5 h-5 rounded-full bg-white border-4 border-accent" />
                  <span className="text-xs font-bold text-accent uppercase tracking-wider mb-1 block">{item.step}</span>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">{item.title}</h3>
                  <p className="text-neutral-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 6. Design System Section */}
          <section id="system" className="mb-24 bg-neutral-900 text-white p-10 rounded-[2.5rem] scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">Design System</h2>
            <p className="text-neutral-400 mb-12 leading-relaxed max-w-2xl">
              To ensure scalability and consistency across the patient app, we established a comprehensive design system covering typography, color palettes, and reusable components.
            </p>

            <div className="space-y-12">
              {/* Color Palette */}
              <div>
                <h3 className="text-xl font-bold mb-6 text-neutral-200">Color Palette</h3>
                <div className="rounded-2xl overflow-hidden border border-white/10">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/7dtcutnj_Color%20Palette.jpg" 
                    alt="NH Care Color Palette" 
                    className="w-full h-auto"
                  />
                </div>
              </div>

              {/* Components Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-6 text-neutral-200">Buttons & Actions</h3>
                  <div className="rounded-2xl overflow-hidden border border-white/10">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/5ycvbkq4_Buttons%20-%20Mobile.jpg" 
                      alt="Button Components" 
                      className="w-full h-auto"
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-6 text-neutral-200">Input Fields</h3>
                  <div className="rounded-2xl overflow-hidden border border-white/10">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/qiftq47w_Input%20fields%20-%20mobile.jpg" 
                      alt="Input Field Components" 
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 7. Module Improvements (Grid) */}
          <section id="modules" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-12">Module Improvements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-blue-50/50 p-8 rounded-[2rem]">
                <h3 className="font-bold text-xl mb-4 text-blue-900">Doctor Discovery</h3>
                <p className="text-blue-800/70 text-sm mb-4">Redesigned listings with smart filters:</p>
                <div className="flex flex-wrap gap-2">
                  {['Specialty', 'Location', 'Language', 'Availability'].map(tag => (
                    <span key={tag} className="bg-white px-3 py-1 rounded-full text-xs font-bold text-blue-600 shadow-sm">{tag}</span>
                  ))}
                </div>
              </div>

              <div className="bg-green-50/50 p-8 rounded-[2rem]">
                <h3 className="font-bold text-xl mb-4 text-green-900">Consultation History</h3>
                <p className="text-green-800/70 text-sm mb-4">Clearer cards displaying:</p>
                <div className="flex flex-wrap gap-2">
                  {['Doctor Details', 'Date & Time', 'Status'].map(tag => (
                    <span key={tag} className="bg-white px-3 py-1 rounded-full text-xs font-bold text-green-600 shadow-sm">{tag}</span>
                  ))}
                </div>
              </div>

              <div className="bg-orange-50/50 p-8 rounded-[2rem]">
                <h3 className="font-bold text-xl mb-4 text-orange-900">Lab Test Booking</h3>
                <p className="text-orange-800/70 text-sm">
                  Introduced <span className="font-bold">Investigation Suggestions</span> to help patients easily book recommended diagnostic tests.
                </p>
              </div>

              <div className="bg-purple-50/50 p-8 rounded-[2rem]">
                <h3 className="font-bold text-xl mb-4 text-purple-900">Home Dashboard</h3>
                <p className="text-purple-800/70 text-sm">
                  Refined for quick access to booking, scheduling, and health records management.
                </p>
              </div>
            </div>
          </section>

          {/* 8. Impact */}
          <section id="impact" className="mb-24 bg-white border border-neutral-100 p-10 rounded-[2.5rem] scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8 text-neutral-900">Product Impact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              {[
                { title: "Faster Doctor Discovery", desc: "Quick identification through improved filtering." },
                { title: "Efficient Re-booking", desc: "Streamlined flows for recurring consultations." },
                { title: "Better Tracking", desc: "Clearer visibility of lab test status and reports." },
                { title: "Design Consistency", desc: "Standardized UI scaling across new modules." }
              ].map((item, i) => (
                <div key={i}>
                  <h3 className="font-bold text-lg mb-2 flex items-center gap-2 text-neutral-900">
                    <CheckCircle className="w-5 h-5 text-accent" /> {item.title}
                  </h3>
                  <p className="text-neutral-500 text-sm leading-relaxed pl-7">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 9. Key Screens (Updated) */}
          <section id="screens" className="mb-20 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">Key Screens</h2>
            <div className="space-y-12">
              <div className="rounded-3xl overflow-hidden shadow-sm border border-neutral-100">
                <img 
                  src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/vdtlkdsj_Existing%20user%20with%20patient%20records_HAPPY%20FLOW.jpg" 
                  alt="Happy Flow - Existing User" 
                  className="w-full h-auto"
                />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-sm border border-neutral-100">
                <img 
                  src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/q420xm36_Section%202.jpg" 
                  alt="Key Screens - Section 2" 
                  className="w-full h-auto"
                />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-sm border border-neutral-100">
                <img 
                  src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/ilkf8pry_Section%203.jpg" 
                  alt="Key Screens - Section 3" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default NHCareCaseStudy;
